export class ApiError extends Error {
    status;
    code;
    constructor(status, message, code) {
        super(message);
        this.status = status;
        this.code = code;
        this.name = 'ApiError';
    }
}
export function createClient({ baseUrl, getBearerToken }) {
    const request = async (path, init) => {
        const hasBody = init?.body != null;
        const incomingHeaders = (init?.headers ?? {});
        // Only send content-type when we actually have a body — Fastify rejects
        // empty bodies that claim to be JSON.
        const headers = { ...incomingHeaders };
        if (hasBody && !Object.keys(headers).some((k) => k.toLowerCase() === 'content-type')) {
            headers['content-type'] = 'application/json';
        }
        const bearer = getBearerToken?.();
        if (bearer &&
            !Object.keys(headers).some((k) => k.toLowerCase() === 'authorization')) {
            headers['authorization'] = `Bearer ${bearer}`;
        }
        const res = await fetch(`${baseUrl}${path}`, {
            credentials: 'include',
            ...init,
            headers,
        });
        if (!res.ok) {
            const body = await res.text().catch(() => '');
            let message = body || res.statusText;
            let code;
            try {
                const parsed = JSON.parse(body);
                if (parsed.message)
                    message = parsed.message;
                if (parsed.code)
                    code = parsed.code;
            }
            catch {
                // body wasn't JSON; fall back to raw text
            }
            throw new ApiError(res.status, message, code);
        }
        if (res.status === 204)
            return undefined;
        return res.json();
    };
    return {
        request,
        baseUrl,
        auth: {
            signup: (input) => request('/api/auth/signup', {
                method: 'POST',
                body: JSON.stringify(input),
            }),
            login: (input) => request('/api/auth/login', {
                method: 'POST',
                body: JSON.stringify(input),
            }),
            logout: () => request('/api/auth/logout', { method: 'POST' }),
            me: () => request('/api/auth/me'),
            forgotPassword: (input) => request('/api/auth/forgot-password', {
                method: 'POST',
                body: JSON.stringify(input),
            }),
            resetPassword: (input) => request('/api/auth/reset-password', {
                method: 'POST',
                body: JSON.stringify(input),
            }),
            adminLogin: (input) => request('/api/auth/admin/login', {
                method: 'POST',
                body: JSON.stringify(input),
            }),
            verifyAdminOtp: (input) => request('/api/auth/admin/verify-otp', {
                method: 'POST',
                body: JSON.stringify(input),
            }),
            // Mint a manager bearer token for impersonation. The cookie is left
            // alone — the caller stashes the returned `impToken` in the new
            // tab's sessionStorage and uses it as the bearer there.
            loginAs: (input) => request('/api/auth/admin/login-as', {
                method: 'POST',
                body: JSON.stringify(input),
            }),
            // Re-issue an admin cookie for the impersonator. The caller (the
            // manager tab) clears its sessionStorage bearer and navigates back
            // to the admin portal.
            exitImpersonation: () => request('/api/auth/admin/exit-impersonation', {
                method: 'POST',
            }),
        },
        admin: {
            listEnterprises: (params) => {
                const qs = new URLSearchParams();
                if (params?.search)
                    qs.set('search', params.search);
                if (params?.page)
                    qs.set('page', String(params.page));
                if (params?.limit)
                    qs.set('limit', String(params.limit));
                const q = qs.toString();
                return request(`/api/admin/enterprises${q ? `?${q}` : ''}`);
            },
            getEnterprise: (id) => request(`/api/admin/enterprises/${encodeURIComponent(id)}`),
            updateEnterprise: (id, patch) => request(`/api/admin/enterprises/${encodeURIComponent(id)}`, {
                method: 'PATCH',
                body: JSON.stringify(patch),
            }),
            listEnterpriseUsers: (id) => request(`/api/admin/enterprises/${encodeURIComponent(id)}/users`),
        },
        dashboard: {
            stats: (params) => {
                const qs = new URLSearchParams();
                if (params?.month)
                    qs.set('month', String(params.month));
                if (params?.year)
                    qs.set('year', String(params.year));
                const q = qs.toString();
                return request(`/api/dashboard/stats${q ? `?${q}` : ''}`);
            },
        },
        agents: {
            list: (params) => {
                const qs = new URLSearchParams();
                if (params?.status)
                    qs.set('status', params.status);
                if (params?.proptree)
                    qs.set('proptree', params.proptree);
                if (params?.sort)
                    qs.set('sort', params.sort);
                if (params?.order)
                    qs.set('order', params.order);
                if (params?.page)
                    qs.set('page', String(params.page));
                if (params?.limit)
                    qs.set('limit', String(params.limit));
                const q = qs.toString();
                return request(`/api/agents${q ? `?${q}` : ''}`);
            },
        },
        inquiries: {
            list: (params) => {
                const qs = new URLSearchParams();
                if (params?.qualification)
                    qs.set('qualification', params.qualification);
                if (params?.rating)
                    qs.set('rating', params.rating);
                if (params?.followUp)
                    qs.set('followUp', params.followUp);
                if (params?.agent)
                    qs.set('agent', params.agent);
                if (params?.listing)
                    qs.set('listing', params.listing);
                if (params?.client)
                    qs.set('client', params.client);
                if (params?.start)
                    qs.set('start', params.start);
                if (params?.end)
                    qs.set('end', params.end);
                if (params?.sort)
                    qs.set('sort', params.sort);
                if (params?.order)
                    qs.set('order', params.order);
                if (params?.page)
                    qs.set('page', String(params.page));
                if (params?.limit)
                    qs.set('limit', String(params.limit));
                const q = qs.toString();
                return request(`/api/inquiries${q ? `?${q}` : ''}`);
            },
            exportCsv: async (params) => {
                const qs = new URLSearchParams();
                if (params?.qualification)
                    qs.set('qualification', params.qualification);
                if (params?.rating)
                    qs.set('rating', params.rating);
                if (params?.followUp)
                    qs.set('followUp', params.followUp);
                if (params?.agent)
                    qs.set('agent', params.agent);
                if (params?.listing)
                    qs.set('listing', params.listing);
                if (params?.client)
                    qs.set('client', params.client);
                if (params?.start)
                    qs.set('start', params.start);
                if (params?.end)
                    qs.set('end', params.end);
                if (params?.sort)
                    qs.set('sort', params.sort);
                if (params?.order)
                    qs.set('order', params.order);
                const q = qs.toString();
                const res = await fetch(`${baseUrl}/api/inquiries/export.csv${q ? `?${q}` : ''}`, { credentials: 'include' });
                if (!res.ok) {
                    const body = await res.text().catch(() => '');
                    throw new ApiError(res.status, body || res.statusText);
                }
                return res.blob();
            },
        },
        clients: {
            listClientsLight: (params) => {
                const qs = new URLSearchParams();
                if (params?.sort)
                    qs.set('sort', params.sort);
                if (params?.order)
                    qs.set('order', params.order);
                if (params?.page)
                    qs.set('page', String(params.page));
                if (params?.limit)
                    qs.set('limit', String(params.limit));
                const q = qs.toString();
                return request(`/api/clients${q ? `?${q}` : ''}`);
            },
        },
        proptrees: {
            listProptrees: (params) => {
                const qs = new URLSearchParams();
                if (params?.status)
                    qs.set('status', params.status);
                if (params?.sort)
                    qs.set('sort', params.sort);
                if (params?.order)
                    qs.set('order', params.order);
                if (params?.page)
                    qs.set('page', String(params.page));
                if (params?.limit)
                    qs.set('limit', String(params.limit));
                const q = qs.toString();
                return request(`/api/proptrees${q ? `?${q}` : ''}`);
            },
            // Agency-default proptree settings — used by the manager portal's
            // /proptree/agency view.
            getAgencyProptree: () => request('/api/proptrees/agency'),
            listAffiliates: (params) => {
                const qs = new URLSearchParams();
                if (params?.status)
                    qs.set('status', params.status);
                if (params?.sort)
                    qs.set('sort', params.sort);
                if (params?.order)
                    qs.set('order', params.order);
                if (params?.page)
                    qs.set('page', String(params.page));
                if (params?.limit)
                    qs.set('limit', String(params.limit));
                const q = qs.toString();
                return request(`/api/proptrees/affiliates${q ? `?${q}` : ''}`);
            },
        },
        listings: {
            list: (params) => {
                const qs = new URLSearchParams();
                if (params?.status)
                    qs.set('status', params.status);
                if (params?.type)
                    qs.set('type', params.type);
                if (params?.sort)
                    qs.set('sort', params.sort);
                if (params?.order)
                    qs.set('order', params.order);
                if (params?.page)
                    qs.set('page', String(params.page));
                if (params?.limit)
                    qs.set('limit', String(params.limit));
                const q = qs.toString();
                return request(`/api/listings${q ? `?${q}` : ''}`);
            },
        },
    };
}
